import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronRight, ChevronLeft } from "lucide-react";

export default function Banners() {
  const nav = useNavigate();
  const [banners, setBanners] = useState([]);
  const [idx, setIdx] = useState(0);
  const [zoomed, setZoomed] = useState(null);
  const timerRef = useRef(null);

  useEffect(() => {
    api.get("/banners").then(r => setBanners(r.data || [])).catch(() => {});
  }, []);

  // Auto-advance every 4s when not zoomed
  useEffect(() => {
    if (zoomed || banners.length <= 1) return;
    timerRef.current = setInterval(() => {
      setIdx(i => (i + 1) % banners.length);
    }, 4000);
    return () => clearInterval(timerRef.current);
  }, [banners.length, zoomed]);

  const goPrev = () => setIdx(i => (i - 1 + banners.length) % banners.length);
  const goNext = () => setIdx(i => (i + 1) % banners.length);

  if (!banners.length) return null;
  const b = banners[idx];

  return (
    <>
      <div className="px-3 mt-3 mb-2 md:max-w-2xl md:mx-auto" data-testid="home-banners">
        <div className="relative w-full aspect-[16/9] rounded-2xl overflow-hidden border border-white/10">
          <AnimatePresence mode="wait" initial={false}>
            <motion.button
              key={b.id}
              type="button"
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -40 }}
              transition={{ duration: 0.45, ease: "easeOut" }}
              onClick={() => setZoomed(b)}
              data-testid={`banner-${idx}`}
              className="absolute inset-0 w-full h-full text-left"
              style={{
                backgroundImage: b.image_url ? `url(${b.image_url})` : undefined,
                backgroundSize: "cover",
                backgroundPosition: "center",
                background: !b.image_url ? `linear-gradient(135deg, ${b.color}33, #0B0F19)` : undefined,
              }}
            >
              <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, transparent 0%, ${b.color}66 60%, #000000cc 100%)` }} />
              {b.highlight && (
                <div className="absolute top-2 left-2 z-10 px-2 py-0.5 rounded-full bg-[#FF2A5F] text-white text-[10px] font-extrabold tracking-wider shadow-lg animate-pulse">
                  IMPORTANT
                </div>
              )}
              <div className="absolute inset-x-0 bottom-0 p-3 z-10">
                <h3 className={`font-outfit font-extrabold text-base leading-tight drop-shadow-lg ${b.highlight ? "animate-pulse" : ""}`} style={{ color: b.color }}>{b.title}</h3>
                {b.body && <p className="text-white/85 text-[11px] mt-1 line-clamp-2">{b.body}</p>}
                {b.cta_label && (
                  <div className={`mt-1.5 inline-flex items-center gap-1 text-[11px] font-bold ${b.highlight ? "text-[#FF7A00]" : "text-white"}`}>
                    {b.cta_label} <ChevronRight className="w-3.5 h-3.5" />
                  </div>
                )}
              </div>
            </motion.button>
          </AnimatePresence>

          {/* Prev / Next arrows */}
          {banners.length > 1 && (
            <>
              <button onClick={goPrev} data-testid="banner-prev"
                className="absolute left-1.5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-black/45 backdrop-blur flex items-center justify-center z-20 active:scale-90">
                <ChevronLeft className="w-4 h-4 text-white" />
              </button>
              <button onClick={goNext} data-testid="banner-next"
                className="absolute right-1.5 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full bg-black/45 backdrop-blur flex items-center justify-center z-20 active:scale-90">
                <ChevronRight className="w-4 h-4 text-white" />
              </button>
            </>
          )}

          {/* Dots indicator */}
          <div className="absolute bottom-1 left-0 right-0 flex items-center justify-center gap-1.5 z-20" data-testid="banner-dots">
            {banners.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                data-testid={`banner-dot-${i}`}
                className={`transition-all rounded-full ${i === idx ? "w-5 h-1.5 bg-white" : "w-1.5 h-1.5 bg-white/50"}`}
              />
            ))}
          </div>

          {/* Counter pill */}
          <div className="absolute top-2 right-2 z-20 px-2 py-0.5 rounded-full bg-black/55 backdrop-blur text-[10px] font-bold text-white">
            {idx + 1} / {banners.length}
          </div>
        </div>
      </div>

      <AnimatePresence>
        {zoomed && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            data-testid="banner-zoom-modal"
            className="fixed inset-0 z-[80] bg-black/90 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setZoomed(null)}>
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.9, opacity: 0 }}
              transition={{ type: "spring", stiffness: 240, damping: 22 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full max-w-md rounded-3xl overflow-hidden border border-white/15"
              style={{
                backgroundImage: zoomed.image_url ? `url(${zoomed.image_url})` : undefined,
                backgroundSize: "cover", backgroundPosition: "center",
                background: !zoomed.image_url ? `linear-gradient(135deg, ${zoomed.color}, #0B0F19)` : undefined,
              }}>
              <button onClick={() => setZoomed(null)} data-testid="banner-zoom-close"
                className="absolute top-3 right-3 z-10 w-9 h-9 rounded-full bg-black/60 backdrop-blur flex items-center justify-center">
                <X className="w-5 h-5" />
              </button>
              <div className="aspect-[4/5] relative">
                <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, transparent 30%, #000000ee 100%)` }} />
                <div className="absolute inset-x-0 bottom-0 p-6 z-10">
                  <h2 className="font-outfit font-extrabold text-3xl leading-tight drop-shadow-lg" style={{ color: zoomed.color }}>
                    {zoomed.title}
                  </h2>
                  <p className="text-white/90 text-sm mt-3 leading-relaxed">{zoomed.body}</p>
                  {zoomed.cta_label && zoomed.cta_url && (
                    <button onClick={() => { setZoomed(null); nav(zoomed.cta_url); }}
                      data-testid="banner-zoom-cta"
                      className="mt-5 w-full py-3 rounded-full font-bold text-black flex items-center justify-center gap-2 active:scale-95"
                      style={{ background: zoomed.color }}>
                      {zoomed.cta_label} <ChevronRight className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
